/**
 * Connection Parameters Types
 *
 * Types for connection parameter sets from connector manifests.
 * Based on LogicAppsUX ConnectionParameterSet structure.
 */

// =============================================================================
// PARAMETER TYPES
// =============================================================================

/**
 * Connection parameter types
 */
export const ParameterType = {
  String: 'string',
  SecureString: 'securestring',
  Bool: 'bool',
  Int: 'int',
  Object: 'object',
  Array: 'array',
  OAuthSetting: 'oauthSetting',
  OAuthConnection: 'oauthconnection',
  ManagedIdentity: 'managedIdentity',
  Gateway: 'gatewaySetting',
  Connection: 'connection',
  ClientCertificate: 'clientCertificate',
} as const;
export type ParameterType = (typeof ParameterType)[keyof typeof ParameterType];

/**
 * Parameter visibility levels
 */
export const ParameterVisibility = {
  Default: 'default',
  Internal: 'internal',
  Advanced: 'advanced',
} as const;
export type ParameterVisibility = (typeof ParameterVisibility)[keyof typeof ParameterVisibility];

// =============================================================================
// PARAMETER DEFINITION
// =============================================================================

/**
 * Constraint definitions for parameters
 */
export interface ParameterConstraints {
  /** Whether the parameter is required */
  required?: 'true' | 'false' | boolean;
  /** Whether the parameter should be hidden */
  hidden?: 'true' | 'false' | boolean;
  /** Whether the parameter should be hidden in the UI (alternate constraint used by some connectors) */
  hideInUI?: 'true' | 'false' | boolean;
  /** Allowed values for dropdown */
  allowedValues?: ParameterAllowedValue[];
  /** Default value */
  default?: unknown;
  /** Regex pattern for validation */
  pattern?: string;
  /** Minimum value (for int type) */
  minimum?: number;
  /** Maximum value (for int type) */
  maximum?: number;
  /** Minimum length (for string type) */
  minLength?: number;
  /** Maximum length (for string type) */
  maxLength?: number;
  /** Capabilities required */
  capability?: string[];
  /** Tab index for UI ordering */
  tabIndex?: number;
  /**
   * Path inside `parameterValues` where this parameter should be nested when
   * serialized to the API. The runtime declares auth-provider parameters
   * (Tenant, ClientId, Secret, Pfx, Identity, Audience, Type, ...) with
   * `PropertyPath = ["authProvider"]`; honoring this collapses them into the
   * `authProvider` block the runtime expects, instead of leaking flat at
   * `parameterValues` root.
   */
  propertyPath?: string[];
  /**
   * Sibling-parameter condition controlling visibility/validation. The runtime
   * places this on `Constraints.DependentParameter` (e.g.
   * `AddActiveDirectoryOAuthManifest()` puts the Secret/Pfx/Password switch
   * here) but earlier portal code only read it from the top-level `param`
   * field. Keep both supported so AAD service-provider connectors don't end up
   * with Secret + Pfx + Password all visible/required simultaneously.
   */
  dependentParameter?: DependentParameter;
}

/**
 * Allowed value for dropdown parameters
 */
export interface ParameterAllowedValue {
  /** Display text */
  text?: string;
  /** Actual value */
  value: unknown;
}

/**
 * UI definition for parameter display
 */
export interface ParameterUIDefinition {
  /** Display name for the parameter */
  displayName?: string;
  /** Description of the parameter */
  description?: string;
  /** Tooltip text */
  tooltip?: string;
  /** Constraints on the parameter */
  constraints?: ParameterConstraints;
}

/**
 * Dependent parameter condition
 */
export interface DependentParameter {
  /** Name of the parameter this depends on */
  parameter: string;
  /** Value that triggers visibility */
  value: unknown;
}

/**
 * OAuth settings for OAuth-type parameters
 */
export interface OAuthSettings {
  /** OAuth client ID */
  clientId?: string;
  /** Identity provider */
  identityProvider?: string;
  /** Required scopes */
  scopes?: string[];
  /** Redirect URL */
  redirectUrl?: string;
  /** Whether first-party auth is available */
  isFirstParty?: boolean;
  /** Custom OAuth parameters */
  customParameters?: Record<string, unknown>;
}

/**
 * Managed identity settings
 */
export interface ManagedIdentitySettings {
  /** Target resource URI */
  resourceUri?: string;
  /** Additional resource URIs */
  additionalResourceUris?: string[];
  /** Required Azure RBAC roles */
  requiredRoles?: string[];
}

/**
 * Gateway settings for on-premises connections
 */
export interface GatewaySettings {
  /** Data source type */
  dataSourceType?: string;
  /** Connection details */
  connectionDetails?: string[];
}

/**
 * Connection parameter definition
 */
export interface ConnectionParameter {
  /** Parameter type */
  type: ParameterType | string;
  /** UI definition */
  uiDefinition?: ParameterUIDefinition;
  /** Visibility level */
  visibility?: ParameterVisibility | string;
  /** Dependent parameter (for conditional visibility) */
  dependentParameter?: DependentParameter;
  /** OAuth settings (for OAuth type) */
  oAuthSettings?: OAuthSettings;
  /** Managed identity settings */
  managedIdentitySettings?: ManagedIdentitySettings;
  /** Gateway settings */
  gateway?: GatewaySettings;
  /** Parameter source */
  parameterSource?: string;
}

// =============================================================================
// PARAMETER SET
// =============================================================================

/**
 * Connection parameter set (represents one auth method)
 */
export interface ConnectionParameterSet {
  /** Unique name for this parameter set */
  name: string;
  /** UI definition for the set */
  uiDefinition?: {
    /** Display name shown to user */
    displayName: string;
    /** Description of this auth method */
    description?: string;
  };
  /** Parameters in this set */
  parameters: Record<string, ConnectionParameter>;
}

/**
 * Connection parameter sets from connector manifest
 */
export interface ConnectionParameterSets {
  /** Default parameter set name */
  uiDefinition?: {
    displayName?: string;
    description?: string;
  };
  /** Available parameter sets */
  values: ConnectionParameterSet[];
}

// =============================================================================
// FORM STATE
// =============================================================================

/**
 * Form field value types
 */
export type FormFieldValue = string | number | boolean | null | undefined | Record<string, unknown> | unknown[];

/**
 * Form values map
 */
export type FormValues = Record<string, FormFieldValue>;

/**
 * Validation error for a field
 */
export interface FieldValidationError {
  /** Error message */
  message: string;
  /** Error type */
  type?: 'required' | 'pattern' | 'minLength' | 'maxLength' | 'min' | 'max' | 'custom';
}

/**
 * Form validation errors
 */
export type FormValidationErrors = Record<string, FieldValidationError>;

/**
 * Form field state
 */
export interface FormFieldState {
  /** Current value */
  value: FormFieldValue;
  /** Whether field has been touched */
  touched: boolean;
  /** Whether field is dirty (changed) */
  dirty: boolean;
  /** Validation error if any */
  error?: FieldValidationError;
}

/**
 * Complete form state
 */
export interface FormState {
  /** Field states */
  fields: Record<string, FormFieldState>;
  /** Whether form is valid */
  isValid: boolean;
  /** Whether form is dirty */
  isDirty: boolean;
  /** Whether form is submitting */
  isSubmitting: boolean;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a parameter is required
 */
export function isParameterRequired(param: ConnectionParameter): boolean {
  const required = param.uiDefinition?.constraints?.required;
  return required === true || required === 'true';
}

/**
 * Check if a parameter should be hidden
 */
export function isParameterHidden(param: ConnectionParameter): boolean {
  const constraints = param.uiDefinition?.constraints;
  const hidden = constraints?.hidden;
  const hideInUI = constraints?.hideInUI;
  return hidden === true || hidden === 'true' || hideInUI === true || hideInUI === 'true';
}

/**
 * Check if a parameter is visible based on dependent parameter
 */
export function isParameterVisible(
  param: ConnectionParameter,
  allValues: FormValues
): boolean {
  // If hidden, never show
  if (isParameterHidden(param)) {
    return false;
  }

  // Internal-visibility params are not user-facing
  if (param.visibility === 'internal') {
    return false;
  }

  // Sibling-parameter condition. Runtime manifests place this under
  // `uiDefinition.constraints.dependentParameter` (e.g. AAD parameter set
  // toggling Secret vs Pfx based on CredentialType). Older fixtures used the
  // top-level `param.dependentParameter` field — read both so neither shape
  // breaks. Constraint-level wins when both are declared.
  const dependent =
    param.uiDefinition?.constraints?.dependentParameter ?? param.dependentParameter;
  if (dependent) {
    const dependentValue = allValues[dependent.parameter];
    return dependentValue === dependent.value;
  }

  // Default: visible
  return true;
}

/**
 * Get the display name for a parameter
 */
export function getParameterDisplayName(
  paramKey: string,
  param: ConnectionParameter
): string {
  return param.uiDefinition?.displayName ?? formatParameterName(paramKey);
}

/**
 * Format a parameter key into a display name
 */
export function formatParameterName(key: string): string {
  // Split on camelCase and underscores
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Get default value for a parameter
 */
export function getDefaultValue(param: ConnectionParameter): FormFieldValue {
  const defaultVal = param.uiDefinition?.constraints?.default;

  if (defaultVal !== undefined) {
    return defaultVal as FormFieldValue;
  }

  // Type-based defaults
  switch (param.type) {
    case ParameterType.Bool:
      return false;
    case ParameterType.Int:
      return 0;
    case ParameterType.Array:
      return [];
    case ParameterType.Object:
      return {};
    default:
      return '';
  }
}

/**
 * Validate a single field value
 */
export function validateFieldValue(
  value: FormFieldValue,
  param: ConnectionParameter
): FieldValidationError | undefined {
  const constraints = param.uiDefinition?.constraints;

  // Required check
  if (isParameterRequired(param)) {
    const isEmpty =
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0);

    if (isEmpty) {
      return {
        message: `${getParameterDisplayName('', param)} is required`,
        type: 'required',
      };
    }
  }

  // String validations
  if (typeof value === 'string' && constraints) {
    // Pattern
    if (constraints.pattern) {
      const regex = new RegExp(constraints.pattern);
      if (!regex.test(value)) {
        return {
          message: 'Invalid format',
          type: 'pattern',
        };
      }
    }

    // Min length
    if (constraints.minLength !== undefined && value.length < constraints.minLength) {
      return {
        message: `Must be at least ${constraints.minLength} characters`,
        type: 'minLength',
      };
    }

    // Max length
    if (constraints.maxLength !== undefined && value.length > constraints.maxLength) {
      return {
        message: `Must be no more than ${constraints.maxLength} characters`,
        type: 'maxLength',
      };
    }
  }

  // Number validations
  if (typeof value === 'number' && constraints) {
    if (constraints.minimum !== undefined && value < constraints.minimum) {
      return {
        message: `Must be at least ${constraints.minimum}`,
        type: 'min',
      };
    }

    if (constraints.maximum !== undefined && value > constraints.maximum) {
      return {
        message: `Must be no more than ${constraints.maximum}`,
        type: 'max',
      };
    }
  }

  return undefined;
}

/**
 * Validate all form values against parameter set
 */
export function validateFormValues(
  values: FormValues,
  parameterSet: ConnectionParameterSet
): FormValidationErrors {
  const errors: FormValidationErrors = {};

  for (const [key, param] of Object.entries(parameterSet.parameters)) {
    // Skip if not visible
    if (!isParameterVisible(param, values)) {
      continue;
    }

    if (
      param?.type?.toLowerCase() === ParameterType.ManagedIdentity.toLowerCase() ||
      param?.type?.toLowerCase() === ParameterType.OAuthSetting.toLowerCase() ||
      param?.type?.toLowerCase() === ParameterType.OAuthConnection.toLowerCase()
    ) {
      continue;
    }

    const error = validateFieldValue(values[key], param);
    if (error) {
      errors[key] = error;
    }
  }

  return errors;
}

/**
 * Get initial form values from parameter set
 */
export function getInitialFormValues(parameterSet: ConnectionParameterSet): FormValues {
  const values: FormValues = {};

  for (const [key, param] of Object.entries(parameterSet.parameters)) {
    values[key] = getDefaultValue(param);
  }

  return values;
}

/**
 * Check if parameter set has OAuth parameters
 */
export function hasOAuthParameter(parameterSet: ConnectionParameterSet): boolean {
  return Object.values(parameterSet.parameters).some(
    (p) => p.type === ParameterType.OAuthSetting || p.type === ParameterType.OAuthConnection
  );
}

/**
 * Check if parameter set has managed identity parameters
 */
export function hasManagedIdentityParameter(parameterSet: ConnectionParameterSet): boolean {
  return Object.values(parameterSet.parameters).some(
    (p) => p.type === ParameterType.ManagedIdentity
  );
}

/**
 * Detect whether the named set is MI-authenticated. Mirrors LogicAppsUX
 * `isConnectionMultiAuthManagedIdentityType`: the connector manifest is the
 * sole source of truth, so callers must load it first or this returns false.
 */
export function isManagedIdentityParameterSet(
  parameterSetName: string | undefined,
  parameterSets: readonly ConnectionParameterSet[] | undefined
): boolean {
  if (!parameterSetName || !parameterSets) return false;
  const matched = parameterSets.find((s) => s.name === parameterSetName);
  return !!matched && hasManagedIdentityParameter(matched);
}

/**
 * Get the OAuth parameter from a parameter set
 */
export function getOAuthParameter(
  parameterSet: ConnectionParameterSet
): [string, ConnectionParameter] | undefined {
  const entry = Object.entries(parameterSet.parameters).find(
    ([, p]) => p.type === ParameterType.OAuthSetting
  );
  return entry;
}
